import React, { useMemo, useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { apiService } from '@/services/api'
import { Download, UploadCloud, RefreshCcw, FileSpreadsheet, AlertCircle, CheckCircle2, X, ArrowLeft } from 'lucide-react'

const headers = [
  'First Name',
  'Last Name',
  'Email',
  'Gender',
  'BirthDate',
  'Mobile Number',
  'Address',
  'Apt Suite',
  'City',
  'State',
  'Postal Code'
]

const pad = (v) => String(v).padStart(2, '0')

const normalizeDate = (value) => {
  if (!value) return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0]
  }
  if (typeof value === 'number') {
    const parsed = XLSX.SSF?.parse_date_code?.(value)
    if (parsed) {
      return `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}`
    }
  }
  const date = new Date(value)
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().split('T')[0]
  }
  return String(value)
}

const pickValue = (row, keys) => {
  for (const key of keys) {
    const raw = row[key]
    if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
      if (key === 'birthdate') return normalizeDate(raw)
      return String(raw).trim()
    }
  }
  return ''
}

const normalizeRow = (row) => {
  const lowerKeysRow = {}
  Object.keys(row).forEach((key) => {
    lowerKeysRow[key.trim().toLowerCase()] = row[key]
  })

  return {
    firstName: pickValue(lowerKeysRow, ['first name', 'firstname']),
    lastName: pickValue(lowerKeysRow, ['last name', 'lastname']),
    email: pickValue(lowerKeysRow, ['email']),
    gender: pickValue(lowerKeysRow, ['gender']),
    birthDate: pickValue(lowerKeysRow, ['birthdate']),
    mobileNumber: pickValue(lowerKeysRow, ['mobile number', 'mobilenumber', 'phone']),
    address: pickValue(lowerKeysRow, ['address']),
    aptSuite: pickValue(lowerKeysRow, ['apt suite', 'aptsuite']),
    city: pickValue(lowerKeysRow, ['city']),
    state: pickValue(lowerKeysRow, ['state']),
    postalCode: pickValue(lowerKeysRow, ['postal code', 'postalcode', 'zipcode'])
  }
}

const validateRow = (row, seenEmails) => {
  const issues = []
  if (!row.firstName) issues.push('First Name is required')
  if (!row.lastName) issues.push('Last Name is required')
  if (!row.email) issues.push('Email is required')
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (row.email && !emailPattern.test(row.email)) issues.push('Email is invalid')
  const emailKey = row.email?.toLowerCase()
  if (emailKey) {
    if (seenEmails.has(emailKey)) issues.push('Duplicate email')
    else seenEmails.add(emailKey)
  }
  return issues
}

const formatCell = (value) => (value === undefined || value === null || value === '' ? '-' : value)

const statusConfig = {
  idle: {
    label: 'Idle',
    badgeClass: 'border-slate-200 bg-slate-100 text-slate-700'
  },
  processing: {
    label: 'Processing',
    badgeClass: 'border-amber-200 bg-amber-100 text-amber-700'
  },
  parsed: {
    label: 'Parsed',
    badgeClass: 'border-blue-200 bg-blue-100 text-blue-700'
  },
  ready: {
    label: 'Ready',
    badgeClass: 'border-emerald-200 bg-emerald-100 text-emerald-700'
  },
  submitted: {
    label: 'Submitted',
    badgeClass: 'border-emerald-200 bg-emerald-100 text-emerald-700'
  }
}

const UserUpload = ({ onBack = null }) => {
  const fileInputRef = useRef(null)
  const [fileName, setFileName] = useState('')
  const [validRows, setValidRows] = useState([])
  const [invalidRows, setInvalidRows] = useState([])
  const [parsingError, setParsingError] = useState('')
  const [status, setStatus] = useState('idle')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  const maxRecords = 100

  const hasData = useMemo(() => validRows.length > 0 || invalidRows.length > 0, [validRows, invalidRows])
  const hasFile = Boolean(fileName)
  const currentStatus = statusConfig[status] || statusConfig.idle

  const handleDownloadTemplate = () => {
    const wsData = [headers]
    const ws = XLSX.utils.aoa_to_sheet(wsData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Template')
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'user-upload-template.xlsx'
    link.click()
    URL.revokeObjectURL(url)
  }

  const resetData = () => {
    setFileName('')
    setValidRows([])
    setInvalidRows([])
    setParsingError('')
    setStatus('idle')
    setMessage('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const processRows = (rows) => {
    const seenEmails = new Set()
    const valid = []
    const invalid = []

    rows.forEach((raw, index) => {
      const normalized = normalizeRow(raw)
      const issues = validateRow(normalized, seenEmails)
      if (issues.length > 0) {
        invalid.push({ index: index + 2, issues, data: normalized })
      } else {
        valid.push(normalized)
      }
    })

    setValidRows(valid)
    setInvalidRows(invalid)
    setStatus(valid.length > 0 && invalid.length === 0 ? 'ready' : 'parsed')
    setMessage('')
  }

  const handleFile = (file) => {
    if (!file) return
    setParsingError('')
    setMessage('')
    setStatus('processing')
    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        if (!rows.length) {
          setParsingError('No rows found in file')
          setStatus('idle')
          return
        }
        if (rows.length > maxRecords) {
          setParsingError(`Maximum ${maxRecords} records allowed per upload`)
          setStatus('idle')
          return
        }
        processRows(rows)
      } catch (err) {
        setParsingError('Unable to read file. Please use the provided template.')
        setStatus('idle')
      }
    }
    reader.onerror = () => {
      setParsingError('Failed to load file')
      setStatus('idle')
    }
    reader.readAsArrayBuffer(file)
  }

  const handleFileInput = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    handleFile(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      setFileName(file.name)
      handleFile(file)
    }
  }

  const handleSubmit = async () => {
    if (invalidRows.length > 0) {
      setMessage('Fix invalid rows before submitting')
      return
    }
    if (validRows.length === 0) {
      setMessage('Upload a file with at least one valid row')
      return
    }

    setSubmitting(true)
    setMessage('')

    try {
      const companyUrl = apiService.getCompanyUrl()
      const emails = validRows.map((row) => row.email)
      const createRes = await apiService.post(apiService.withCompanyEndpoint('/createbulkclients', companyUrl), { emails })
      if (!createRes.success) {
        setMessage(createRes.error || 'Failed to create clients')
        setSubmitting(false)
        return
      }

      const createdList =
        createRes.data?.created_clients ||
        createRes.data?.clients ||
        createRes.data?.data ||
        createRes.data ||
        []
      const emailToId = new Map()

      if (Array.isArray(createdList)) {
        createdList.forEach((item) => {
          const emailKey = (item.email || item.client_email || '').toLowerCase()
          const uid = item.user_id || item.id || item.userId || item.client_id
          if (emailKey && uid) emailToId.set(emailKey, uid)
        })
      } else if (createdList && typeof createdList === 'object') {
        Object.entries(createdList).forEach(([emailKey, uid]) => {
          if (emailKey && uid) emailToId.set(emailKey.toLowerCase(), uid)
        })
      }

      const clientsPayload = validRows
        .map((row) => {
          const userId = emailToId.get(row.email.toLowerCase())
          if (!userId) return null
          return {
            user_id: userId,
            email: row.email,
            firstname: row.firstName,
            lastname: row.lastName,
            gender: row.gender,
            birthdate: row.birthDate,
            mobilephone: row.mobileNumber,
            address: row.address,
            aptsuite: row.aptSuite,
            city: row.city,
            state: row.state,
            postalcode: row.postalCode
          }
        })
        .filter(Boolean)

      if (clientsPayload.length === 0) {
        setMessage('Created clients but no IDs returned to update')
        setSubmitting(false)
        return
      }

      const updateRes = await apiService.put(apiService.withCompanyEndpoint('/updateBulkClients', companyUrl), { clients: clientsPayload })
      if (!updateRes.success) {
        setMessage(updateRes.error || 'Failed to update clients')
        setSubmitting(false)
        return
      }

      setStatus('submitted')
      setMessage('Users submitted successfully')
    } catch (err) {
      setMessage('Submission failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="uploader-page">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={handleFileInput}
      />

      <div className="app-page-hero">
        <div className="absolute top-0 right-0 h-36 w-36 rounded-full hidden" />
        <div className="app-page-hero-content space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            ) : null}
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-semibold">Bulk User Upload</h1>
            <p className="max-w-3xl text-base leading-7">
              Upload multiple users with the Excel template, validate rows before submission, and keep the workflow aligned with the rest of the uploader workspace.
            </p>
          </div>
        </div>
      </div>

      <div className="uploader-surface space-y-6 pb-8">
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6 md:p-7">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <p className="max-w-3xl text-sm text-slate-600">
                Upload multiple users at once with the Excel template. Keep headers unchanged, review validations, and submit clean data.
              </p>
              <p className="text-xs text-slate-500">Required headers: {headers.join(', ')}. Maximum {maxRecords} records per upload.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${currentStatus.badgeClass}`}>
                {currentStatus.label}
              </span>
              {hasFile && (
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> File loaded
                </span>
              )}
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
              <Button variant={hasFile ? 'outline' : 'primary'} onClick={() => fileInputRef.current?.click()}>
                <UploadCloud className="h-4 w-4 mr-2" />
                {hasFile ? 'Replace File' : 'Choose File'}
              </Button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-500">Status</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{currentStatus.label}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-500">Valid rows</div>
              <div className="mt-1 text-sm font-semibold text-emerald-700">{validRows.length}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-500">Invalid rows</div>
              <div className="mt-1 text-sm font-semibold text-red-700">{invalidRows.length}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-500">Selected file</div>
              <div className="mt-1 truncate text-sm font-semibold text-slate-900" title={fileName || 'None'}>{fileName || 'None'}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {parsingError && (
        <Card className="border-red-200 bg-red-50/50 shadow-sm">
          <CardContent className="flex items-center gap-2 text-red-700 text-sm py-4">
            <AlertCircle className="h-4 w-4" />
            <span>{parsingError}</span>
          </CardContent>
        </Card>
      )}

      {message && (
        <Card className={status === 'submitted' ? 'border-green-200 bg-green-50/50 shadow-sm' : 'border-amber-200 bg-amber-50/50 shadow-sm'}>
          <CardContent className={`flex items-center gap-2 text-sm py-4 ${status === 'submitted' ? 'text-green-700' : 'text-amber-700'}`}>
            {status === 'submitted' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <span>{message}</span>
          </CardContent>
        </Card>
      )}

      {hasData ? (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="space-y-6 xl:col-span-2">
            {validRows.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center justify-between text-green-700">
                    <span className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> Valid Data ({validRows.length} records)</span>
                    {status === 'submitted' && <span className="text-sm text-green-700">Submitted</span>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        {headers.map((h) => (
                          <th key={h} className="text-left px-4 py-3 font-semibold text-slate-700">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {validRows.map((row, idx) => (
                        <tr key={idx} className="border-t hover:bg-slate-50/70">
                          <td className="px-4 py-2">{formatCell(row.firstName)}</td>
                          <td className="px-4 py-2">{formatCell(row.lastName)}</td>
                          <td className="px-4 py-2">{formatCell(row.email)}</td>
                          <td className="px-4 py-2">{formatCell(row.gender)}</td>
                          <td className="px-4 py-2">{formatCell(row.birthDate)}</td>
                          <td className="px-4 py-2">{formatCell(row.mobileNumber)}</td>
                          <td className="px-4 py-2">{formatCell(row.address)}</td>
                          <td className="px-4 py-2">{formatCell(row.aptSuite)}</td>
                          <td className="px-4 py-2">{formatCell(row.city)}</td>
                          <td className="px-4 py-2">{formatCell(row.state)}</td>
                          <td className="px-4 py-2">{formatCell(row.postalCode)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {invalidRows.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" /> Invalid Data ({invalidRows.length} records)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Row</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Issues</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invalidRows.map((row) => (
                        <tr key={row.index} className="border-t hover:bg-red-50/50">
                          <td className="px-4 py-2">{row.index}</td>
                          <td className="px-4 py-2 text-red-700 space-y-1">
                            {row.issues.map((issue) => (
                              <div key={issue} className="flex items-center gap-1">
                                <X className="h-3 w-3" />
                                <span>{issue}</span>
                              </div>
                            ))}
                          </td>
                          <td className="px-4 py-2">{formatCell(row.data.email)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-4">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <UploadCloud className="h-5 w-5 text-fitness-primary" />
                  File Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold">Loaded file</div>
                    <div className="truncate text-slate-600">{fileName || 'None'}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()}>{hasFile ? 'Replace File' : 'Choose File'}</Button>
                  <Button variant="outline" onClick={handleDownloadTemplate}>
                    <Download className="h-4 w-4 mr-2" />
                    Get Template
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <FileSpreadsheet className="h-5 w-5 text-fitness-primary" />
                  Submission
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-600">
                  Submit only after all rows are valid. Invalid rows are listed below for quick fixes.
                </p>
                <Button variant="success" disabled={submitting || validRows.length === 0 || invalidRows.length > 0} onClick={handleSubmit} className="w-full">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {submitting ? 'Submitting...' : 'Submit Users'}
                </Button>
                <Button variant="outline" onClick={resetData} className="w-full">
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Clear Data
                </Button>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  Submission is enabled only when there are valid rows and zero invalid rows.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Card
            className={`xl:col-span-2 border-2 border-dashed shadow-sm transition-colors ${
              dragActive ? 'border-fitness-primary bg-fitness-primary/5' : 'border-slate-200'
            }`}
            onDragOver={(e) => {
              e.preventDefault()
              setDragActive(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              setDragActive(false)
            }}
            onDrop={handleDrop}
          >
            <CardContent className="p-10 text-center space-y-3">
              <UploadCloud className="h-12 w-12 text-slate-400 mx-auto" />
              <div className="text-xl font-semibold text-slate-900">Drop your Excel file here</div>
              <div className="text-sm text-slate-600">Accepted: .xlsx, .xls, .csv</div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant={hasFile ? 'outline' : 'primary'} onClick={() => fileInputRef.current?.click()}>
                  {hasFile ? 'Replace File' : 'Choose File'}
                </Button>
                <Button variant="outline" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Get Template
                </Button>
              </div>
              {fileName && (
                <div className="text-sm text-slate-700 flex items-center justify-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span>Loaded: {fileName}</span>
                </div>
              )}
              {status === 'processing' && <div className="text-sm text-slate-600">Processing file...</div>}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <FileSpreadsheet className="h-5 w-5 text-fitness-primary" />
                Submission
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">
                Submit only after all rows are valid. Invalid rows are listed below for quick fixes.
              </p>
              <Button variant="success" disabled={submitting || validRows.length === 0 || invalidRows.length > 0} onClick={handleSubmit} className="w-full">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {submitting ? 'Submitting...' : 'Submit Users'}
              </Button>
              <Button variant="outline" onClick={resetData} className="w-full">
                <RefreshCcw className="h-4 w-4 mr-2" />
                Clear Data
              </Button>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                Submission is enabled only when there are valid rows and zero invalid rows.
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <FileSpreadsheet className="h-5 w-5 text-fitness-primary" />
            How to use Bulk Upload
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-sm text-slate-700">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><span className="font-semibold">1.</span> Download the template and keep the headers unchanged.</div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><span className="font-semibold">2.</span> Fill user details. First Name, Last Name, and Email are required.</div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><span className="font-semibold">3.</span> Upload the completed file to review valid and invalid rows.</div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><span className="font-semibold">4.</span> Fix invalid rows if any, then submit to create and update users.</div>
        </CardContent>
      </Card>
      </div>
    </div>
  )
}

export default UserUpload
